import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Minus, Share2, Copy, Check,
  MessageCircle, Twitter, X, Loader2, Flame, Award, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  energy: 'Energy', sleep: 'Sleep', immunity: 'Immunity', stress: 'Stress',
  weight: 'Weight', heart: 'Heart', digestion: 'Digestion', joints: 'Joints',
  hair_skin: 'Hair & Skin', brain_focus: 'Brain', mood: 'Mood', vision: 'Vision',
};

const BAR_COLORS = {
  energy: '#F59E0B', sleep: '#6366F1', immunity: '#10B981', stress: '#EC4899',
  weight: '#3B82F6', heart: '#EF4444', digestion: '#14B8A6', joints: '#F97316',
  hair_skin: '#8B5CF6', brain_focus: '#6366F1', mood: '#FBBF24', vision: '#0EA5E9',
};

function SnapshotCardPreview({ data }) {
  if (!data) return null;
  const improved = data.changes.filter(c => c.change > 0);
  const top = improved.slice(0, 5);

  return (
    <div
      className="rounded-2xl overflow-hidden w-full max-w-sm mx-auto"
      style={{ background: 'linear-gradient(145deg, #0F172A 0%, #1E293B 100%)' }}
      data-testid="progress-snapshot-card"
    >
      {/* Header */}
      <div className="px-6 pt-6 pb-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
              <span className="text-white font-bold text-xs">V</span>
            </div>
            <span className="text-xs font-medium text-white/60">vitala.me</span>
          </div>
          <span className="text-[10px] text-white/40">{data.days_tracked} days</span>
        </div>
        <h2 className="text-lg font-bold text-white mt-3">
          {data.first_name}'s Progress
        </h2>
        <p className="text-xs text-white/50 mt-0.5">{data.improved_count} areas improved</p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 px-6 pb-3">
        <div className="text-center rounded-lg py-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <p className="text-base font-bold text-emerald-400">{data.total_improvement_points}</p>
          <p className="text-[9px] text-white/40">Points Gained</p>
        </div>
        <div className="text-center rounded-lg py-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <p className="text-base font-bold text-amber-400">{data.current_streak}</p>
          <p className="text-[9px] text-white/40">Day Streak</p>
        </div>
        <div className="text-center rounded-lg py-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <p className="text-base font-bold text-sky-400">{data.assessments_count}</p>
          <p className="text-[9px] text-white/40">Assessments</p>
        </div>
      </div>

      {/* Before/After bars */}
      <div className="px-6 pb-4 space-y-2">
        {top.map(c => {
          const color = BAR_COLORS[c.category] || '#6B7280';
          const label = CATEGORY_LABELS[c.category] || c.category;
          return (
            <div key={c.category}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] text-white/60 capitalize">{label}</span>
                <span className="text-[10px] font-semibold text-emerald-400">+{c.change}%</span>
              </div>
              <div className="relative h-3 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                {/* First score (faded) */}
                <div
                  className="absolute inset-y-0 left-0 rounded-full opacity-30"
                  style={{ width: `${c.first}%`, backgroundColor: color }}
                />
                {/* Latest score */}
                <div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ width: `${c.latest}%`, backgroundColor: color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-white/5 text-center">
        <p className="text-[10px] text-white/40">Discover your own supplement plan at</p>
        <p className="text-xs font-semibold text-emerald-400">vitala.me</p>
      </div>
    </div>
  );
}

export function ProgressSnapshotWidget({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/share/progress-snapshot`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      console.error('Progress snapshot error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  if (loading || !data?.has_snapshot) return null;

  const improved = data.changes.filter(c => c.change > 0);
  const declined = data.changes.filter(c => c.change < 0);
  const hasChanges = data.changes.length > 0;

  const trendIcon = data.overall_trend === 'improving'
    ? <TrendingUp className="w-4 h-4 text-emerald-500" />
    : data.overall_trend === 'declining'
    ? <TrendingDown className="w-4 h-4 text-red-400" />
    : <Minus className="w-4 h-4 text-gray-400" />;

  const trendColor = data.overall_trend === 'improving' ? 'text-emerald-600' : data.overall_trend === 'declining' ? 'text-red-500' : 'text-gray-500';

  const handleCopyLink = () => {
    const link = data.referral_link || 'https://vitala.me';
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const text = `I've improved ${data.improved_count} health areas in ${data.days_tracked} days with Vitala! Check yours: ${data.referral_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareTwitter = () => {
    const text = `${data.improved_count} areas improved, ${data.total_improvement_points} points gained in ${data.days_tracked} days on my supplement journey with @VitalaHealth! Get your plan:`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(data.referral_link)}`, '_blank');
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-border overflow-hidden"
        data-testid="progress-snapshot-widget"
      >
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-[#1A1A1A]">Your Progress Snapshot</h3>
                <p className="text-xs text-muted-foreground">{data.days_tracked} days tracked</p>
              </div>
            </div>
            <div className={`flex items-center gap-1 text-xs font-medium ${trendColor}`}>
              {trendIcon}
              <span className="capitalize">{data.overall_trend}</span>
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 rounded-lg bg-emerald-50">
              <p className="text-lg font-bold text-emerald-700">{data.improved_count}</p>
              <p className="text-[10px] text-emerald-600">Improved</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-amber-50">
              <p className="text-lg font-bold text-amber-700">{data.total_improvement_points > 0 ? '+' : ''}{data.total_improvement_points}</p>
              <p className="text-[10px] text-amber-600">Points</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-sky-50">
              <p className="text-lg font-bold text-sky-700">{data.current_streak}</p>
              <p className="text-[10px] text-sky-600">Streak</p>
            </div>
          </div>

          {/* Top changes */}
          {hasChanges && (
            <div className="space-y-1.5">
              {data.changes.slice(0, 4).map(c => {
                const color = BAR_COLORS[c.category] || '#6B7280';
                const label = CATEGORY_LABELS[c.category] || c.category;
                const isUp = c.change > 0;
                return (
                  <div key={c.category} className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-16 truncate capitalize">{label}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden relative">
                      <div className="absolute inset-y-0 left-0 rounded-full opacity-25" style={{ width: `${c.first}%`, backgroundColor: color }} />
                      <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${c.latest}%`, backgroundColor: color }} />
                    </div>
                    <span className={`text-[10px] font-semibold w-8 text-right ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
                      {isUp ? '+' : ''}{c.change}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Share CTA */}
        <div className="border-t border-border">
          <button
            onClick={() => setShowShareModal(true)}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
            data-testid="share-progress-btn"
          >
            <span className="text-sm font-medium text-primary flex items-center gap-2">
              <Share2 className="w-4 h-4" /> Share Your Progress
            </span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </motion.div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="progress-share-modal"
            >
              <div className="p-5 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold text-[#1A1A1A]">Share Your Progress</h3>
                <button onClick={() => setShowShareModal(false)} className="text-muted-foreground hover:text-[#1A1A1A]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5">
                <SnapshotCardPreview data={data} />

                <div className="mt-5 space-y-2">
                  <p className="text-xs text-muted-foreground mb-2 text-center">Share via</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleShareWhatsApp}
                      className="flex items-center justify-center gap-2 p-3 rounded-xl bg-green-50 text-green-700 hover:bg-green-100 transition-colors text-sm font-medium"
                      data-testid="progress-share-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </button>
                    <button
                      onClick={handleShareTwitter}
                      className="flex items-center justify-center gap-2 p-3 rounded-xl bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium"
                      data-testid="progress-share-twitter"
                    >
                      <Twitter className="w-4 h-4" /> X / Twitter
                    </button>
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors text-sm font-medium"
                    data-testid="progress-share-copy"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy Referral Link'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
